import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { EventFieldsDropdown } from "src/eventsCatalogue/filters/EventFieldsDropdown";
import { EventFilterCondition } from "src/eventsCatalogue/filters/EventFilterCondition";
import {
  DEFAULT_JUNCTION,
  MAX_CONDITIONS_PER_GROUP,
} from "src/eventsCatalogue/filters/constants";
import {
  EventFilterFormData,
  Junction,
} from "src/eventsCatalogue/filters/types";
import { buildFilterCondition } from "src/eventsCatalogue/filters/utils";

type EventFilterGroupProps = {
  eventType: string;
  groupIndex: number;
  mode: "where" | "junction";
  onRemove: (() => void) | undefined;
};

export const EventFilterGroup: React.FC<EventFilterGroupProps> = ({
  eventType,
  groupIndex,
  mode,
  onRemove,
}) => {
  const form = useFormContext<EventFilterFormData>();
  const {
    fields: conditions,
    append,
    remove,
  } = useFieldArray<EventFilterFormData>({
    name: `filter_condition.conditions.${groupIndex}.conditions`,
  });

  const handleConditionJunctionChange = (selectedJunction: Junction) => {
    conditions.forEach((_, idx) => {
      if (idx > 1) {
        form.setValue(
          `filter_condition.conditions.${groupIndex}.conditions.${idx}.junction`,
          selectedJunction,
        );
      }
    });
  };

  const handleAddCondition = () => {
    // Get the current junction from form state to avoid stale values
    // If there are multiple conditions, use the junction from the second condition (index 1)
    // Otherwise, default to DEFAULT_JUNCTION
    const currentJunction: Junction =
      conditions.length > 1
        ? (form.getValues(
            `filter_condition.conditions.${groupIndex}.conditions.1.junction`,
          ) as Junction) || DEFAULT_JUNCTION
        : DEFAULT_JUNCTION;
    append(buildFilterCondition(currentJunction));
  };

  const handleRemoveCondition = (index: number) => {
    if (conditions.length > 1) {
      remove(index);
    }
  };

  return (
    <div className="flex gap-x-4">
      <div className="basis-[72px] py-1.5">
        {mode === "where" ? (
          <span className="text-gray-700 font-inter-medium-12px">Where</span>
        ) : (
          <Controller
            name={`filter_condition.conditions.${groupIndex}.junction`}
            render={({ field }) => (
              <EventFieldsDropdown
                dataLoc={`group-${groupIndex}-junction`}
                disabled={groupIndex > 1}
                eventType={eventType}
                selectedKey={field.value}
                type="junction"
                onSelect={(key) => {
                  field.onChange(key);
                  if (groupIndex === 1) {
                    // Sync all subsequent groups when first group junction changes
                    const groups = form.getValues(
                      "filter_condition.conditions",
                    );
                    groups.forEach((_, idx) => {
                      if (idx > 1) {
                        form.setValue(
                          `filter_condition.conditions.${idx}.junction`,
                          key as Junction,
                        );
                      }
                    });
                  }
                }}
              />
            )}
          />
        )}
      </div>
      <div className="flex flex-grow flex-col gap-y-3 rounded-lg bg-gray-50 p-3">
        {onRemove && (
          <div className="flex justify-end">
            <Button
              dataLoc={`delete-group-${groupIndex}`}
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={onRemove}
            >
              Delete filter group
            </Button>
          </div>
        )}
        {conditions.map((condition, index) => (
          <EventFilterCondition
            key={condition.id}
            canRemove={conditions.length > 1}
            conditionIndex={index}
            eventType={eventType}
            groupIndex={groupIndex}
            mode={index === 0 ? "where" : "junction"}
            onJunctionChange={
              index === 1 ? handleConditionJunctionChange : undefined
            }
            onRemove={() => handleRemoveCondition(index)}
          />
        ))}
        <div>
          <Button
            dataLoc={`add-condition-${groupIndex}`}
            disabled={conditions.length >= MAX_CONDITIONS_PER_GROUP}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={handleAddCondition}
          >
            Add filter
          </Button>
        </div>
      </div>
    </div>
  );
};
